import React from 'react';
import { Star, Award, Sparkles, Trophy, Gem, Crown } from 'lucide-react';

interface Differentiator {
  title: string;
  description: string;
  impact?: string;
  category?: 'expertise' | 'achievement' | 'innovation' | 'leadership' | 'certification' | 'other';
  priority?: 'high' | 'medium' | 'low';
}

interface UniqueDifferentiatorsProps {
  differentiators: Differentiator[];
  title?: string;
}

const UniqueDifferentiators: React.FC<UniqueDifferentiatorsProps> = ({
  differentiators,
  title = 'Unique Differentiators'
}) => {
  const getCategoryStyle = (category?: string) => {
    switch (category) {
      case 'expertise':
        return {
          icon: Gem,
          gradient: 'from-blue-500 to-cyan-500',
          bg: 'bg-blue-50',
          border: 'border-blue-300',
          text: 'text-blue-700',
          badge: 'bg-blue-100 text-blue-700'
        };
      case 'achievement':
        return {
          icon: Trophy,
          gradient: 'from-amber-500 to-yellow-500',
          bg: 'bg-amber-50',
          border: 'border-amber-300',
          text: 'text-amber-700',
          badge: 'bg-amber-100 text-amber-700'
        };
      case 'innovation':
        return {
          icon: Sparkles,
          gradient: 'from-purple-500 to-pink-500',
          bg: 'bg-purple-50',
          border: 'border-purple-300',
          text: 'text-purple-700',
          badge: 'bg-purple-100 text-purple-700'
        };
      case 'leadership':
        return {
          icon: Crown,
          gradient: 'from-red-500 to-orange-500',
          bg: 'bg-red-50',
          border: 'border-red-300',
          text: 'text-red-700',
          badge: 'bg-red-100 text-red-700'
        };
      case 'certification':
        return {
          icon: Award,
          gradient: 'from-green-500 to-emerald-500',
          bg: 'bg-green-50',
          border: 'border-green-300',
          text: 'text-green-700',
          badge: 'bg-green-100 text-green-700'
        };
      default:
        return {
          icon: Star,
          gradient: 'from-indigo-500 to-blue-500',
          bg: 'bg-indigo-50',
          border: 'border-indigo-300',
          text: 'text-indigo-700',
          badge: 'bg-indigo-100 text-indigo-700'
        };
    }
  };

  const getPriorityBadge = (priority?: string) => {
    switch (priority) {
      case 'high':
        return <span className="px-2 py-0.5 text-xs font-semibold bg-red-100 text-red-700 rounded-full">High Impact</span>;
      case 'medium':
        return <span className="px-2 py-0.5 text-xs font-semibold bg-amber-100 text-amber-700 rounded-full">Medium Impact</span>;
      case 'low':
        return <span className="px-2 py-0.5 text-xs font-semibold bg-gray-100 text-gray-700 rounded-full">Standard</span>;
      default:
        return null;
    }
  };

  const sortByPriority = (a: Differentiator, b: Differentiator) => {
    const priorityOrder = { high: 3, medium: 2, low: 1 };
    const aPriority = priorityOrder[a.priority || 'low'];
    const bPriority = priorityOrder[b.priority || 'low'];
    return bPriority - aPriority;
  };

  const sortedDifferentiators = [...differentiators].sort(sortByPriority);

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
      <div className="px-6 py-5 border-b border-gray-200 bg-gradient-to-r from-yellow-50 via-amber-50 to-orange-50">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-gradient-to-br from-amber-500 to-orange-500 rounded-lg shadow-md">
            <Star className="h-6 w-6 text-white" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
            <p className="text-sm text-gray-600">Standout qualities and competitive advantages</p>
          </div>
        </div>
      </div>

      <div className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {sortedDifferentiators.map((diff, index) => {
            const style = getCategoryStyle(diff.category);
            const Icon = style.icon;

            return (
              <div
                key={index}
                className={`relative ${style.bg} border-2 ${style.border} rounded-xl p-6 hover:shadow-xl transition-all duration-300 group overflow-hidden`}
              >
                <div className={`absolute top-0 right-0 w-32 h-32 bg-gradient-to-br ${style.gradient} opacity-10 rounded-full -mr-16 -mt-16 group-hover:scale-150 transition-transform duration-500`}></div>

                <div className="relative z-10">
                  <div className="flex items-start justify-between mb-4">
                    <div className={`p-3 bg-gradient-to-br ${style.gradient} rounded-lg shadow-md group-hover:scale-110 transition-transform`}>
                      <Icon className="h-6 w-6 text-white" />
                    </div>
                    {diff.priority && getPriorityBadge(diff.priority)}
                  </div>

                  <div className="mb-3">
                    <h4 className={`font-bold text-lg ${style.text} mb-2 flex items-center gap-2`}>
                      {diff.title}
                      {diff.category && (
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded ${style.badge} uppercase`}>
                          {diff.category}
                        </span>
                      )}
                    </h4>
                    <p className="text-gray-700 text-sm leading-relaxed">
                      {diff.description}
                    </p>
                  </div>

                  {diff.impact && (
                    <div className="mt-4 pt-3 border-t-2 border-gray-200">
                      <div className="flex items-start gap-2">
                        <Sparkles className={`h-4 w-4 ${style.text} flex-shrink-0 mt-0.5`} />
                        <div>
                          <p className="text-xs font-semibold text-gray-700 mb-1">Business Impact:</p>
                          <p className="text-xs text-gray-600 italic">{diff.impact}</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div className={`absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r ${style.gradient}`}></div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="px-6 py-4 bg-gradient-to-r from-gray-50 to-white border-t border-gray-200">
        <div className="flex flex-wrap items-center justify-center gap-4 text-xs">
          <div className="flex items-center gap-2">
            <Gem className="h-4 w-4 text-blue-500" />
            <span className="text-gray-600 font-medium">Expertise</span>
          </div>
          <div className="flex items-center gap-2">
            <Trophy className="h-4 w-4 text-amber-500" />
            <span className="text-gray-600 font-medium">Achievement</span>
          </div>
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-purple-500" />
            <span className="text-gray-600 font-medium">Innovation</span>
          </div>
          <div className="flex items-center gap-2">
            <Crown className="h-4 w-4 text-red-500" />
            <span className="text-gray-600 font-medium">Leadership</span>
          </div>
          <div className="flex items-center gap-2">
            <Award className="h-4 w-4 text-green-500" />
            <span className="text-gray-600 font-medium">Certification</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UniqueDifferentiators;
